import { AppRoutes } from "../../../routes/AppRoutes";
import { useNavigateWithParams } from "../../../hooks/useNavigateWithParams";

import { Box } from "@mui/material";
import Text from "../../atoms/Text/Text";
import Title from "../../atoms/Title/Title";
import Chip from "../../atoms/Chip/Chip";
import { formatPrice } from "../../../helpers/currencyHelper";
import { formatDate } from "../../../helpers/formatDate";
import { useTranslation } from "react-i18next";
import StyledButton from "../../atoms/StyledButton/StyledButton";

type SubscriptionOrOptionItem = {
  type: string;
  id?: number;
  status: string;
  period: number;
  next_charge_at?: string;
  code?: string;
  amount: number;
  currency: string;
};

interface Props {
  item: SubscriptionOrOptionItem;
  onActivate?: () => void;
  onEdit?: () => void;
  isDisabled?: boolean;
}

const ManageSubscriptionsOptionsBlock = ({ item, onActivate, onEdit, isDisabled }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigateWithParams();

  const isValidDate = (dateString?: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const daysDifferenceFromNow = (date: string): number => {
    const nextChargeDate = new Date(`${date}T00:00:00Z`);
    const now = new Date();
    const currentUtcDate = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate()
    ));
    return (nextChargeDate.getTime() - currentUtcDate.getTime()) / (1000 * 60 * 60 * 24);
  };

  return (
    <Box className="ManageSubscriptionsOptionsBlock">
      <Box className="subscriptionHeader">
        <Box className="subscriptionTitle">
          <Title variant="h2">
            {
              item.type === "subscription" && item.period
                ? (item.period % 12 === 0
                  ? t("subscription:SUBSCRIPTION_TITLE.YEAR", { count: item.period / 12, period: item.period / 12 })
                  : t("subscription:SUBSCRIPTION_TITLE.MONTH", { count: item.period, period: item.period })
                )
                : item.type === "option"
                  ? item.code === "warranty"
                    ? t("subscription:MANAGE_PAGE.ACCOUNT_OPTIONS.WARRANTY.TITLE")
                    : item.code === "premium_pack"
                      ? t("subscription:MANAGE_PAGE.ACCOUNT_OPTIONS.PREMIUM_PACK.TITLE")
                      : null
                  : null
            }
          </Title>
        </Box>
        <Box className="subscriptionAmount">
          <Text
            children={
              item.type === "option" ?
                item.amount === 0
                  ? t("subscription.FREE")
                  : <>
                    {formatPrice(item.amount / 100, item.currency)}
                    {item.period === 1 && <> {t("subscription:COMMON_TEXT.MONTH")}</>}
                  </>
                : null
            }
          />
        </Box>
      </Box>
      {item.type === "option" && (
        <>
          {item.code === "warranty" && (
            <Box className="subscriptionAdvantages">
              <Text children={t("subscription:MANAGE_PAGE.ACCOUNT_OPTIONS.WARRANTY.DESCRIPTION")} />
            </Box>
          )}
          {item.code === "premium_pack" && (
            <Box className="subscriptionAdvantages">
              <Text children={t("subscription:MANAGE_PAGE.ACCOUNT_OPTIONS.PREMIUM_PACK.DESCRIPTION")} />
            </Box>
          )}
        </>
      )}
      <Box className="subscriptionStatus">
        {item.type === "subscription" && (
          <>
            {item.status === "active"
              && item.next_charge_at
              && isValidDate(item.next_charge_at)
              && (
                <>
                  <Box className="statusContainer">
                    <Box className="statusLine">
                      <Text children={t("subscription:MANAGE_PAGE.SUBSCRIPTION_STATUS.ACTIVE.TITLE")} />
                      <Box className="statusDot green" />
                    </Box>
                    <Text children={
                      daysDifferenceFromNow(item.next_charge_at) <= 0 ? (
                        t("subscription:MANAGE_PAGE.SUBSCRIPTION_STATUS.ACTIVE.DESCRIPTION_PAST", {
                          period: item.period % 12 === 0
                            ? t("subscription:PERIOD.YEAR", { count: item.period / 12, period: item.period / 12 })
                            : t("subscription:PERIOD.MONTH", { count: item.period, period: item.period }),
                          price: item.amount === 0 ? t("subscription:FREE") : formatPrice(item.amount / 100, item.currency)
                        })) : (
                        t("subscription:MANAGE_PAGE.SUBSCRIPTION_STATUS.ACTIVE.DESCRIPTION", {
                          date: formatDate(item.next_charge_at),
                          period: item.period % 12 === 0
                            ? t("subscription:PERIOD.YEAR", { count: item.period / 12, period: item.period / 12 })
                            : t("subscription:PERIOD.MONTH", { count: item.period, period: item.period }),
                          price: item.amount === 0 ? t("subscription:FREE") : formatPrice(item.amount / 100, item.currency)
                        })
                      )
                    } />
                  </Box>
                  <StyledButton
                    className="left small white"
                    children={t("subscription:MANAGE_PAGE.EDIT.BUTTON")}
                    onClick={onEdit}
                    disabled={isDisabled}
                  />
                </>
              )}

            {item.status === "canceled"
              && item.next_charge_at
              && isValidDate(item.next_charge_at)
              && daysDifferenceFromNow(item.next_charge_at) >= 0
              && (
                <>
                  <Box className="statusContainer">
                    <Box className="statusLine">
                      <Text children={t("subscription:MANAGE_PAGE.SUBSCRIPTION_STATUS.CANCELED.TITLE")} />
                      <Box className="statusDot red" />
                    </Box>
                    <Text children={t("subscription:MANAGE_PAGE.SUBSCRIPTION_STATUS.CANCELED.DESCRIPTION", {
                      date: formatDate(item.next_charge_at!)
                    })} />
                  </Box>
                  <StyledButton
                    className="left small"
                    children={t("subscription:MANAGE_PAGE.REACTIVATE.BUTTON")}
                    onClick={onActivate}
                    disabled={isDisabled}
                  />
                </>
              )}

            {item.status === "canceled"
              && (!item.next_charge_at || !isValidDate(item.next_charge_at) || daysDifferenceFromNow(item.next_charge_at) < 0)
              && (
                <>
                  <Box className="statusContainer">
                    <Text children={t("subscription:MANAGE_PAGE.SUBSCRIPTION_STATUS.EXPIRED.DESCRIPTION", {
                      date: formatDate(item.next_charge_at!)
                    })} />
                  </Box>
                  <StyledButton
                    className="left small"
                    children={t("subscription:MANAGE_PAGE.SUBSCRIBE_AGAIN.BUTTON")}
                    onClick={() => navigate(AppRoutes.SUBSCRIPTIONS)}
                    disabled={isDisabled}
                  />
                </>
              )}
          </>
        )}

        {item.type === "option" && (
          <>
            {item.status === "active"
              && item.next_charge_at
              && isValidDate(item.next_charge_at)
              && (
                <>
                  <Box className="statusContainer">
                    <Box className="statusLine">
                      <Text children={t("subscription:MANAGE_PAGE.OPTION_STATUS.ACTIVE.TITLE")} />
                      <Box className="statusDot green" />
                    </Box>
                    <Text children={
                      daysDifferenceFromNow(item.next_charge_at) <= 0 ? (
                        t("subscription:MANAGE_PAGE.OPTION_STATUS.ACTIVE.DESCRIPTION_PAST", {
                          period: item.period % 12 === 0
                            ? t("subscription:PERIOD.YEAR", {count : item.period/12, period : item.period/12})
                            : t("subscription:PERIOD.MONTH", {count : item.period, period : item.period}),
                          price: item.amount === 0 ? t("subscription:FREE") : formatPrice(item.amount / 100, item.currency)
                        })) : (
                        t("subscription:MANAGE_PAGE.OPTION_STATUS.ACTIVE.DESCRIPTION", {
                          date: formatDate(item.next_charge_at),
                          period: item.period % 12 === 0
                            ? t("subscription:PERIOD.YEAR", {count : item.period/12, period : item.period/12})
                            : t("subscription:PERIOD.MONTH", {count : item.period, period : item.period}),
                          price: item.amount === 0 ? t("subscription:FREE") : formatPrice(item.amount / 100, item.currency)
                        })
                      )
                    } />
                  </Box>
                  <StyledButton
                    className="left small white"
                    children={t("subscription:MANAGE_PAGE.CANCEL.BUTTON")}
                    onClick={onEdit}
                    disabled={isDisabled}
                  />
                </>
              )}

            {item.status === "active"
              && (!item.next_charge_at || !isValidDate(item.next_charge_at))
              && (
                <>
                  <Box className="statusContainer">
                    <Text children={t("subscription:MANAGE_PAGE.OPTION_STATUS.ACTIVE_NO_DATE.DESCRIPTION")} />
                  </Box>
                  <StyledButton
                    className="left small white"
                    children={t("subscription:MANAGE_PAGE.CANCEL.BUTTON")}
                    onClick={onEdit}
                    disabled={isDisabled}
                  />
                </>
              )}

            {item.status === "canceled"
              && item.next_charge_at
              && isValidDate(item.next_charge_at)
              && daysDifferenceFromNow(item.next_charge_at) >= 0
              && (
                <>
                  <Box className="statusContainer">
                    <Box className="statusLine">
                      <Text children={t("subscription:MANAGE_PAGE.OPTION_STATUS.CANCELED.TITLE")} />
                      <Box className="statusDot red" />
                    </Box>
                    <Text children={t("subscription:MANAGE_PAGE.OPTION_STATUS.CANCELED.DESCRIPTION", {
                      date: formatDate(item.next_charge_at!)
                    })} />

                  </Box>
                  <StyledButton
                    className="left small"
                    children={t("subscription:MANAGE_PAGE.REACTIVATE.BUTTON")}
                    onClick={onActivate}
                    disabled={isDisabled}
                  />
                </>
              )}
            {item.status === "canceled"
              && (!item.next_charge_at || !isValidDate(item.next_charge_at) || daysDifferenceFromNow(item.next_charge_at) < 0)
              && (
                <>
                  <Box className="statusContainer">
                    <Text children={t("subscription:MANAGE_PAGE.OPTION_STATUS.EXPIRED.DESCRIPTION")} />
                  </Box>
                  <StyledButton
                    className="left small"
                    children={t("subscription:MANAGE_PAGE.ACTIVATE.BUTTON")}
                    onClick={onActivate}
                    disabled={isDisabled}
                  />
                </>
              )}

            {!item.status && (
              <>
                {item.code === "premium_pack" && (
                  <Chip text={t("subscription:MANAGE_PAGE.FIRST_MONTH_FREE")} isActive={true} />
                )}
                <StyledButton
                  className="left small"
                  children={t("subscription:MANAGE_PAGE.ACTIVATE.BUTTON")}
                  onClick={onActivate}
                  disabled={isDisabled}
                />
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ManageSubscriptionsOptionsBlock;